import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFloorPlan } from "../api/create-floor-plan";
import { deleteFloorPlan } from "../api/delete-floor-plan";
import { reorderFloorPlans } from "../api/reorder-floor-plans";
import { updateFloorPlan } from "../api/update-floor-plan";
import { FLOOR_PLANS_QUERY_KEY } from "./use-floor-plans";
import type { FloorPlan } from "~/entities/floor-plan/types";

export function useCreateFloorPlan(branchUid: string, versionId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name: string; image?: File }) =>
      createFloorPlan({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLOOR_PLANS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useUpdateFloorPlan(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      floorPlanId: number;
      name?: string;
      image?: File;
      iconSize?: number;
      labelSize?: number;
    }) => updateFloorPlan({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLOOR_PLANS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useDeleteFloorPlan(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (floorPlanId: number) =>
      deleteFloorPlan({ branchUid, versionId, floorPlanId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLOOR_PLANS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useReorderFloorPlans(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (floorPlanIds: number[]) =>
      reorderFloorPlans({ branchUid, versionId, floorPlanIds }),
    onSuccess: (_data, floorPlanIds) => {
      // 저장 직후 reordering=false 로 전환되면 렌더가 쿼리 데이터로 바뀌는데, refetch 전이라
      // 옛 순서가 한 프레임 보이며 깜빡인다. refetch 를 기다리는 대신 캐시를 새 순서로 즉시
      // 덮어써 플리커를 없앤다(invalidate 는 백그라운드로 서버 순서 재확인).
      queryClient.setQueryData<FloorPlan[]>(
        [FLOOR_PLANS_QUERY_KEY, branchUid, versionId],
        (old = []) => {
          const byId = new Map(old.map((fp) => [fp.id, fp]));
          const ordered = floorPlanIds
            .map((id) => byId.get(id))
            .filter((fp): fp is FloorPlan => fp !== undefined);
          const seen = new Set(floorPlanIds);
          return [...ordered, ...old.filter((fp) => !seen.has(fp.id))];
        },
      );
      queryClient.invalidateQueries({ queryKey: [FLOOR_PLANS_QUERY_KEY, branchUid, versionId] });
    },
  });
}
